import * as React from "react"
import { Loader2, Search as SearchIcon, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Input } from "./input"

export interface SearchProps
  extends Omit<React.ComponentProps<"input">, "onChange" | "type"> {
  /** Valeur contrôlée. */
  value: string
  /** Notifié à chaque frappe avec la nouvelle valeur (sans event). */
  onValueChange: (value: string) => void
  /** Affiche un spinner à droite (recherche réseau en cours). */
  loading?: boolean
  /** Affiche un bouton « effacer » quand la valeur est non vide. */
  onClear?: () => void
  /** Libellé accessible du bouton « effacer ». */
  clearLabel?: string
  /** Classe du conteneur (pour piloter fond/ombre/arrondi). */
  containerClassName?: string
  /** Classe de l'input. */
  className?: string
}

/**
 * Search — champ de recherche packagé (HELP-DRAWER §9.2).
 *
 * Input + loupe + style, pour le Help Drawer ET les pages d'aide pleine page
 * (qui réassemblaient `Input` + loupe à la main). N'est PAS utilisé par la
 * palette ⌘K de SEARCH (qui a son propre `CommandInput` cmdk) — neutre/teal,
 * focus `--primary`.
 */
export const Search = React.forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      value,
      onValueChange,
      loading,
      onClear,
      clearLabel = "Effacer",
      containerClassName,
      className,
      disabled,
      ...props
    },
    ref,
  ) => {
    const showClear = !!onClear && value.length > 0 && !loading
    return (
      <div className={cn("relative", containerClassName)}>
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={ref}
          type="text"
          value={value}
          disabled={disabled}
          onChange={(e) => onValueChange(e.target.value)}
          className={cn(
            "h-11 border-0 bg-transparent pl-10 pr-10 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0",
            className,
          )}
          {...props}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
        {showClear && (
          // Clear-field « pastille » (cercle plein) : plus visible qu'une croix nue
          // ET visuellement distinct de la croix de FERMETURE du drawer (croix nue
          // dans le coin) — on ne lit plus « deux croix identiques ».
          <button
            type="button"
            onClick={onClear}
            aria-label={clearLabel}
            className="absolute right-2.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-muted-foreground/15 text-muted-foreground transition-colors hover:bg-muted-foreground/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    )
  },
)
Search.displayName = "Search"
